import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { Socket, io } from 'socket.io-client';
import { InboxMessageAcceptanceStatus } from '@app/features/teacher/inbox/inbox-item/inbox-item.component';
import { IFilterOptions } from '@app/features/teacher/inbox/inbox-filter/inbox-filter-smart.component';
import { INoteCreationDTO, INoteDTO } from '@app/infrastructure/models/note';
import { IStudentAnswerDTO } from '@app/infrastructure/models/answer';
import { environment } from '@env/environment';
import { IStudentAssignmentDTO } from '@app/infrastructure/models/student-assignment';

export interface Message {
  source: string;
  content: string;
}

export class TeacherNoteDto {
  studentId: string;
  recipientId: string;
  dayBlockId: number;
  message: string;
  type: string;
}

@Injectable({
  providedIn: 'root',
})
export class WebsocketsService {
  public messages: Subject<Message> = new Subject<Message>();
  public teacherNote: Subject<INoteDTO> = new Subject<INoteDTO>();
  public teacherInbox: Subject<INoteDTO[]> = new Subject<INoteDTO[]>();
  public notificationContent: Subject<IStudentAssignmentDTO> =
    new Subject<IStudentAssignmentDTO>();
  private inboxSocket: Socket;
  private notificationSocket: Socket;

  constructor() {
    this.inboxSocket = io(environment.apiRoute, {
      path: '/inbox',
      autoConnect: false,
      extraHeaders: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    this.notificationSocket = io(environment.apiRoute, {
      path: '/notifications',
      autoConnect: false,
      extraHeaders: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
  }

  connectToInbox(teacherId: string) {
    if (this.inboxSocket.disconnected) {
      this.inboxSocket = io(environment.apiRoute, {
        path: '/inbox',
        autoConnect: false,
        extraHeaders: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      this.inboxSocket.on('connect', () => {
        // I do not think this is necessary since we just connect directly to the classroom inbox channel, but keeping it just in case
        this.inboxSocket.emit('joinTeacherRoom');
      });
      this.inboxSocket.connect();
    }
    this.getTeacherNotes(teacherId);
    this.receiveNote();
  }

  disconnectToInbox() {
    if (this.inboxSocket.listenersAny()) this.inboxSocket.removeAllListeners();
    this.inboxSocket.disconnect();
  }

  connectToNotifications() {
    if (this.notificationSocket.disconnected) {
      this.notificationSocket = io(environment.apiRoute, {
        path: '/notifications',
        autoConnect: false,
        extraHeaders: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      this.notificationSocket.on('connect', () => {
        this.notificationSocket.emit('joinNotificationRoom');
      });
      this.notificationSocket.connect();
    }
    this.sendAssignmentCompleteNotifications();
  }

  disconnectNotifications() {
    if (this.notificationSocket.listenersAny())
      this.notificationSocket.removeAllListeners();
    this.notificationSocket.disconnect();
  }

  sendAcceptanceStatus($event: InboxMessageAcceptanceStatus) {
    this.inboxSocket.emit('sendAcceptStatus', $event);
  }

  receiveNote(): void {
    this.inboxSocket.on('newClassroomNote', (newNote: INoteDTO) => {
      this.teacherNote.next(newNote);
    });
  }

  send(message: string, payload: any) {
    this.inboxSocket.emit(message, payload);
  }

  sendUpdatePoints(note) {
    this.inboxSocket.emit('sendUpdateNotePoints', note);
  }

  sendToggleStar(note): void {
    this.inboxSocket.emit('toggleStar', note);
  }

  updateNote(noteId: string, payload: INoteCreationDTO) {
    this.inboxSocket.emit('updateNote', { noteId, payload });
  }

  sendTeacherNotesFilterOptions(userId: string, queryParams: IFilterOptions) {
    const reset = queryParams?.reset || false;
    if (queryParams && !reset) {
      this.inboxSocket.emit('sendTeacherNotesFilterOptions', {
        userId,
        queryParams,
      });
    } else {
      this.inboxSocket.emit('sendTeacherNotesFilterOptions', { userId });
    }
  }

  teacherRespondToNote(noteId: number, response: string): void {
    this.inboxSocket.emit('respondToNote', { noteId, response });
  }

  teacherUpdateResponseToNote(
    id: string,
    response: string,
    noteId: number,
  ): void {
    this.inboxSocket.emit('updateNoteResponse', { id, response, noteId });
  }

  getTeacherNotes(teacherId: string): void {
    this.inboxSocket.on(`getTeacherNotes-${teacherId}`, (notes: INoteDTO[]) => {
      this.teacherInbox.next(notes);
    });
  }

  sendAssignmentCompleteNotifications(): void {
    this.notificationSocket.on(
      'assignmentCompleted',
      (assignment: IStudentAssignmentDTO) => {
        this.notificationContent.next(assignment);
      },
    );
  }
}
